import { useState } from 'react';
import { categories } from '@/mocks/categories';

const leftCol = categories.slice(0, 5);
const midCol = categories.slice(5, 10);
const rightCol = categories.slice(10, 14);

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      setEmail('');
      return;
    }

    setStatus('loading');

    try {
      const body = new URLSearchParams();
      body.append('email', email.trim());
      body.append('formId', 'd91o2r80sqa2sn3dn8t0');

      const res = await fetch('https://readdy.ai/api/form/d91o2r80sqa2sn3dn8t0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <footer className="w-full bg-secondary-50 py-16 md:py-24 px-6 md:px-14">
      <div className="max-w-[1280px] mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="lg:w-[35%]">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-[1.2] mb-4">
              Pandabuy
              <br />
              Spreadsheet Wiki
            </h3>
            <p className="text-foreground-500 text-sm leading-relaxed max-w-sm mb-8">
              Your trusted source for curated pandabuy spreadsheet data. We organize
              verified product information, quality ratings, and pricing data to help
              you shop smarter through your preferred agents.
            </p>

            <div>
              <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-3">
                Get Updates
              </p>
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="newsletter-form"
                className="flex flex-col sm:flex-row gap-3"
              >
                <div className="honeypot-field">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                </div>
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="flex-1 h-12 px-4 bg-transparent border-b border-foreground-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-6 h-12 rounded-lg border border-foreground-300 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all duration-300 disabled:opacity-50 cursor-pointer"
                >
                  {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
              {status === 'success' && (
                <p className="text-accent-600 text-xs mt-2">Thanks for subscribing! Check your inbox.</p>
              )}
              {status === 'error' && (
                <p className="text-red-500 text-xs mt-2">Something went wrong. Please try again.</p>
              )}
            </div>
          </div>

          <div className="flex-1 grid grid-cols-3 gap-8">
            <div>
              <h4 className="font-semibold text-foreground-900 text-sm mb-4">Categories</h4>
              <ul className="space-y-2.5">
                {leftCol.map((cat) => (
                  <li key={cat.slug}>
                    <a
                      href={cat.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-foreground-500 text-sm hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground-900 text-sm mb-4">&nbsp;</h4>
              <ul className="space-y-2.5">
                {midCol.map((cat) => (
                  <li key={cat.slug}>
                    <a
                      href={cat.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-foreground-500 text-sm hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground-900 text-sm mb-4">&nbsp;</h4>
              <ul className="space-y-2.5">
                {rightCol.map((cat) => (
                  <li key={cat.slug}>
                    <a
                      href={cat.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-foreground-500 text-sm hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-foreground-200/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-foreground-400 text-xs">
            &copy; 2026 pandabuy.wiki. All rights reserved. This site provides informational data only.
          </p>
          <div className="flex items-center gap-6">
            <a href="/" className="text-foreground-400 text-xs hover:text-foreground-700 transition-colors">
              Home
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-foreground-400 text-xs hover:text-foreground-700 transition-colors"
            >
              Pandabuy Sheet
            </a>
            <a href="/pandabuy-spreadsheet-guide-2026" className="text-foreground-400 text-xs hover:text-foreground-700 transition-colors">
              Blog
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}